import { observer } from "mobx-react-lite";

import {
  dataAttribute,
  DataAttributes,
  FontIcon,
  FontSizes,
  Link,
  Stack,
  Text
} from "@bps/fluent-ui";
import { SettlementStatus } from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { useBookingCalendarScreenContext } from "@modules/booking/screens/booking-calendar/context/BookingCalendarScreenContext.tsx";
import { CalendarEvent } from "@stores/booking/models/CalendarEvent.ts";
import { AppointmentStatusText } from "@ui-components/RefText.tsx";

import {
  OWING_ICON,
  SETTLED_ICON
} from "../booking-calendar-event/BookingCalendarEvent.types.ts";
import { BillingStatus } from "./AppointmentFilter.types.ts";
import { getLinkText, getSelectedFilterStyles } from "./utils.ts";

interface AppointmentBillingStatusFilterProps {
  status: SettlementStatus | BillingStatus;
  handleClick: () => void;
  events: CalendarEvent[];
}

export const AppointmentBillingStatusFilter: React.FC<AppointmentBillingStatusFilterProps> =
  observer(({ status, handleClick, events }) => {
    const { appointmentsFilter } = useBookingCalendarScreenContext();

    const getIconForInvoiceBillingStatus = () => {
      const lookup = new Map<BillingStatus | SettlementStatus, string>([
        [BillingStatus.Unbilled, "M365InvoicingLogo"],
        [SettlementStatus.Owing, OWING_ICON],
        [SettlementStatus.Settled, SETTLED_ICON]
      ]);

      const iconName = lookup.get(status);

      if (iconName) {
        return (
          <FontIcon
            iconName={iconName}
            styles={{
              root: {
                fontSize: FontSizes.large,
                verticalAlign: "middle"
              }
            }}
          />
        );
      }
      return;
    };

    const getTextForInvoiceBillingStatus = () => {
      const lookup = new Map<BillingStatus | SettlementStatus, string>([
        [BillingStatus.Unbilled, "Unbilled"],
        [SettlementStatus.Owing, "Owing"],
        [SettlementStatus.Settled, "Paid"]
      ]);

      const textForStatus = lookup.get(status);

      return textForStatus && <Text>{textForStatus}</Text>;
    };

    return (
      <Stack
        horizontal
        tokens={{ childrenGap: 10 }}
        {...dataAttribute(DataAttributes.Element, `today-appts-${status}`)}
        styles={getSelectedFilterStyles(status, appointmentsFilter)}
      >
        <Stack.Item>{getIconForInvoiceBillingStatus()}</Stack.Item>
        <Stack.Item>
          {getTextForInvoiceBillingStatus()}
          <AppointmentStatusText code={status} />:
        </Stack.Item>
        {events.length > 0 ? (
          <Link
            onClick={handleClick}
            {...dataAttribute(
              DataAttributes.Element,
              `today-appts-${status}-button`
            )}
          >
            {getLinkText(events.length, status, appointmentsFilter)}
          </Link>
        ) : (
          <Text>0</Text>
        )}
      </Stack>
    );
  });
